import type { Terminal as CoreTerminal } from "@/States/Terminals";
import { getFileName } from "@/Utils/commonExportFunctions";
import type { TimeFilter } from "@/components/v2/DatePicker/constants";
import { EXCEL_FORMAT } from "@/constants/general";
import { translate } from "@/translations/functions/hook";

import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import type { Terminal as DwhTerminal } from "server/dwhApi/terminals/types.js";

import { NEEDS_ATTENTION_RATE_THRESHOLD } from "../../../../components/v2/BuildingSelector/constants";
import { BUILDING_LIST_COLUMNS } from "./constants";
import { type BuildingListRow, BuildingStatus } from "./types";

const getStatusOf = (rate: number): BuildingStatus =>
  rate >= NEEDS_ATTENTION_RATE_THRESHOLD
    ? BuildingStatus.ON_TRACK
    : BuildingStatus.NEEDS_ATTENTION;

type Props = {
  terminalsData?: DwhTerminal[];
  selectedBuildingIds: string[] | null;
  allTerminals: CoreTerminal[];
};
export const getBuildingListRows = ({
  terminalsData,
  selectedBuildingIds,
  allTerminals,
}: Props) => {
  const terminals = (terminalsData || []).filter(
    (terminal) =>
      !selectedBuildingIds || selectedBuildingIds.includes(terminal.terminalId),
  );

  return terminals.map((terminal) => {
    const rate = terminal.sortingDegree * 100;
    const coreTerminal = allTerminals.find((t) => t.id === terminal.terminalId);

    const [lat, lng] = (coreTerminal?.coordinates || "")
      .replace(/ /g, "")
      .split(",");

    return {
      terminalId: terminal.terminalId,
      name: terminal.terminalName,
      rate,
      waste: terminal.totalWasteWeight.quantity,
      wasteUnit: terminal.totalWasteWeight.unit,
      tenants: terminal.totalCustomersAcrossTerminal,
      contamination: terminal.totalContaminatedOccurrences,
      status: getStatusOf(rate),
      lat: lat ? Number(lat) : null,
      lng: lng ? Number(lng) : null,
    };
  });
};

type ExportProps = {
  timeframe: TimeFilter;
  rows: BuildingListRow[];
};

const generateExcelFile = ({ rows }: ExportProps) => {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet(
    translate("portfolioPage:buildingListExportSheetTitle"),
  );

  sheet.columns = BUILDING_LIST_COLUMNS.map((column) => ({
    header: translate(column.label),
    key: column.key,
    width: 25,
  }));

  sheet.getRow(1).font = {
    bold: true,
  };

  sheet.addRows(
    rows.map((row) => ({
      name: row.name,
      rate: row.rate,
      waste: `${row.waste.toLocaleString()} ${row.wasteUnit}`,
      contamination: row.contamination,
      tenants: row.tenants,
      status: translate(`portfolioPage:buildingListStatus${row.status}`),
    })),
  );

  return workbook;
};

export const exportExcel = async (props: ExportProps) => {
  const fileTitle = getFileName(
    "portfolioPage:buildingListExportFileName",
    props.timeframe,
  );
  const workbook = generateExcelFile(props);

  const xls = await workbook.xlsx.writeBuffer();
  saveAs(
    new Blob([xls], {
      type: EXCEL_FORMAT,
    }),
    fileTitle,
  );
};
